import { useEffect, useState, useCallback, useMemo } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { format, addDays, startOfWeek, isSameDay, endOfWeek } from 'date-fns'
import { utcSlotContainsLocalTime } from '../utils/timezone'
import { toZonedTime } from 'date-fns-tz'
import { isActiveProposal } from '../utils/matchStatus'
import { expandRecurrence } from '../utils/recurrence'
import { proposalInvolvesPlayer } from '../utils/proposalParticipants'
import services from '../services'
import toastr from 'toastr'

// detect user's zone once
const TZ = Intl.DateTimeFormat().resolvedOptions().timeZone

const asDate = (v) => (v instanceof Date ? v : new Date(v))

const getCurrentTeamId = (divisionInfo) => {
  const currentPlayer = (divisionInfo?.roster || []).find(
    (player) => Number(player.id) === Number(divisionInfo?.player_id),
  )
  return currentPlayer?.partner?.team_id || null
}

/**
 * Custom hook for managing weekly calendar view state and data.
 * Handles data fetching, URL synchronization, and calendar interactions.
 *
 * @param {Object} [options={}] - Configuration options
 * @param {boolean} [options.skip=false] - If true, skips data fetching and URL synchronization.
 *                                         Use when external data is being provided to avoid
 *                                         unnecessary API calls and side effects.
 * @param {number} [options.startHour=6] - Starting hour for calendar display (0-23)
 * @param {number} [options.endHour=22] - Ending hour for calendar display (0-23)
 * @returns {Object} Weekly calendar data and controls
 */
export function useWeeklyCalendarData(options = {}) {
  const { skip = false, startHour = 6, endHour = 22 } = options
  const { leagueId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()

  // Loading state
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Data states
  const [userAvailability, setUserAvailability] = useState([])
  const [opponentAvailability, setOpponentAvailability] = useState([])
  const [unplayedOpponents, setUnplayedOpponents] = useState([])
  const [matchProposals, setMatchProposals] = useState([])
  const [allMatches, setAllMatches] = useState([])
  const [divisionRoster, setDivisionRoster] = useState([])
  const [currentUserPlayerId, setCurrentUserPlayerId] = useState(null)
  const [divisionInfo, setDivisionInfo] = useState(null)

  // Week navigation state - start with current week, let effect sync with URL
  const [currentWeek, setCurrentWeek] = useState(() => startOfWeek(new Date()))

  // Expand recurring availabilities for the current week's date range
  const expandedUserAvailability = useMemo(() => {
    if (!userAvailability || userAvailability.length === 0) {
      return []
    }

    // Get the range for the current week
    const rangeStart = currentWeek
    const rangeEnd = endOfWeek(currentWeek)

    // Expand all availabilities
    const expanded = []
    userAvailability.forEach((availability) => {
      const slots = expandRecurrence(availability, rangeStart, rangeEnd)
      expanded.push(...slots)
    })

    return expanded
  }, [userAvailability, currentWeek])

  // Expand recurring availabilities for opponents
  const expandedOpponentAvailability = useMemo(() => {
    if (!opponentAvailability || opponentAvailability.length === 0) {
      return []
    }

    // Get the range for the current week
    const rangeStart = currentWeek
    const rangeEnd = endOfWeek(currentWeek)

    // Expand all opponent availabilities
    const expanded = []
    opponentAvailability.forEach((availability) => {
      const slots = expandRecurrence(availability, rangeStart, rangeEnd)
      expanded.push(...slots)
    })

    return expanded
  }, [opponentAvailability, currentWeek])

  // Calendar configuration constants
  const TIME_SLOTS = useMemo(() => {
    const slots = []
    // Use provided startHour and endHour, ensuring valid range
    const start = Math.max(0, Math.min(23, startHour))
    const end = Math.min(23, Math.max(start + 1, endHour))

    for (let hour = start; hour <= end; hour++) {
      slots.push({ hour, minute: 0 })
      if (hour < end) {
        slots.push({ hour, minute: 30 })
      }
    }
    return slots
  }, [startHour, endHour])

  const DAYS = useMemo(
    () => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    [],
  )

  // Data loading function
  const loadAvailabilityData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      // First, get division info to have league_id and division_id
      let divisionData = null
      try {
        const leagueResponse = await services.league.getMyDivision(leagueId)
        if (leagueResponse.success) {
          divisionData = leagueResponse.data
          setDivisionInfo(divisionData)

          // Convert allMatches datetimes to local
          const matches =
            leagueResponse.data.matches
              ?.filter((match) => match.match_datetime)
              .map((match) => ({
                ...match,
                match_datetime: match.match_datetime
                  ? new Date(match.match_datetime)
                  : null,
              })) || []
          setAllMatches(matches)

          // Also save the roster data for opponent name lookup
          if (leagueResponse.data.roster) {
            setDivisionRoster(leagueResponse.data.roster)
          }

          // Save current user's player ID
          if (leagueResponse.data.player_id) {
            setCurrentUserPlayerId(leagueResponse.data.player_id)
          }
        } else {
          setAllMatches([])
          setDivisionRoster([])
        }
      } catch {
        // Error loading division info - silently continue
      }

      // Load availability data with league/division context
      const response = divisionData
        ? await services.availability.getLeagueAvailability(
            leagueId,
            divisionData.division_id,
          )
        : await services.availability.getLeagueAvailability(leagueId)
      if (response.success) {
        const parsedUserAvailability = (
          response.data.userAvailability || []
        ).map((slot) => ({
          ...slot,
          start_time: slot.start_time ? new Date(slot.start_time) : null,
          end_time: slot.end_time ? new Date(slot.end_time) : null,
        }))
        setUserAvailability(parsedUserAvailability)
        setOpponentAvailability(
          (response.data.opponentAvailability || []).map((slot) => ({
            ...slot,
            start_time: slot.start_time ? new Date(slot.start_time) : null,
            end_time: slot.end_time ? new Date(slot.end_time) : null,
          })),
        )
        setUnplayedOpponents(response.data.unplayedOpponents || [])
      }

      // Load match proposals with league/division context
      try {
        const proposalsResponse = divisionData
          ? await services.matchProposal.getReceivedProposals(
              parseInt(leagueId),
              divisionData.division_id,
            )
          : await services.matchProposal.getReceivedProposals()

        // proposalsResponse is standardized by BaseService

        if (proposalsResponse.success) {
          const raw = proposalsResponse.data || []
          // Filter to only show active proposals and map dates
          const activeProposals = raw
            .filter(isActiveProposal)
            .map((proposal) => ({
              ...proposal,
              proposed_time: proposal.proposed_time
                ? new Date(proposal.proposed_time)
                : null,
            }))

          if (activeProposals.length < raw.length) {
            console.warn(
              'Client-side filtered inactive proposals - service should have filtered these',
              {
                total: raw.length,
                active: activeProposals.length,
                filtered: raw.length - activeProposals.length,
              },
            )
          }

          setMatchProposals(activeProposals)
        } else {
          // Proposals fetch not successful
        }
      } catch {
        // Error loading match proposals - silently continue
      }
    } catch (err) {
      // Failed to load availability data
      setError(err.message || 'Failed to load availability data')
    } finally {
      setLoading(false)
    }
    // Dependency array intentionally minimal to avoid eslint warning
  }, [leagueId])

  // Initialize data loading (skip if requested)
  useEffect(() => {
    if (!skip) {
      loadAvailabilityData()
    }
  }, [leagueId, loadAvailabilityData, skip])

  // URL parameter synchronization - sync FROM URL TO state on mount and URL changes
  // Skip URL synchronization when skip flag is set
  useEffect(() => {
    if (skip) return

    const weekParam = searchParams.get('week')

    if (weekParam) {
      try {
        // Parse date as local date to avoid timezone issues
        // weekParam should be in format 'yyyy-MM-dd'
        const [year, month, day] = weekParam.split('-').map(Number)
        const date = new Date(year, month - 1, day) // month is 0-indexed
        const weekStart = startOfWeek(date)

        if (!isNaN(weekStart.getTime())) {
          const currentWeekStr = format(currentWeek, 'yyyy-MM-dd')
          const newWeekStr = format(weekStart, 'yyyy-MM-dd')

          if (currentWeekStr !== newWeekStr) {
            setCurrentWeek(weekStart)
          }
          return // Don't set URL if we successfully parsed it
        }
      } catch {
        // Error parsing week parameter - fall through to set current week
      }
    }

    // Either no week param or invalid week param - set to current week
    const thisWeek = startOfWeek(new Date())
    const currentWeekStr = format(currentWeek, 'yyyy-MM-dd')
    const thisWeekStr = format(thisWeek, 'yyyy-MM-dd')

    if (currentWeekStr !== thisWeekStr) {
      setCurrentWeek(thisWeek)
    }

    setSearchParams({ week: thisWeekStr }, { replace: true })
  }, [searchParams, setSearchParams, currentWeek, skip]) // Include skip in dependencies

  // Pre-computed availability map for performance
  const availabilityMap = useMemo(() => {
    if (!expandedUserAvailability || expandedUserAvailability.length === 0)
      return new Map()

    const map = new Map()
    expandedUserAvailability.forEach((slot) => {
      // ⚡ convert the UTC instant into the user's local wall-clock hour/min
      const slotStart = toZonedTime(slot.start_time, TZ)
      const slotEnd = toZonedTime(slot.end_time, TZ)

      let current = new Date(slotStart)
      while (current < slotEnd) {
        const dateKey = format(current, 'yyyy-MM-dd')
        const hour = current.getHours()
        const minute = current.getMinutes()
        const key = `${dateKey}-${hour}-${minute}`

        if (!map.has(key)) {
          map.set(key, [])
        }
        map.get(key).push(slot)

        current.setMinutes(current.getMinutes() + 30)
      }
    })

    return map
  }, [expandedUserAvailability])

  // Derived data calculations
  const getWeekDates = useCallback(() => {
    return DAYS.map((_, index) => addDays(currentWeek, index))
  }, [currentWeek, DAYS])

  const weekDates = getWeekDates()
  const firstDayOfWeek = weekDates[0]
  const lastDayOfWeek = weekDates[6]

  // Helper function to check if two dates/times are the same
  const isSameDateTime = useCallback((date1, date2) => {
    const d1 = new Date(date1)
    const d2 = new Date(date2)
    return (
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate() &&
      d1.getHours() === d2.getHours() &&
      d1.getMinutes() === d2.getMinutes()
    )
  }, [])

  const weekProposals = useMemo(() => {
    const now = new Date()
    // First filter by current week and status
    const currentWeekProposals = matchProposals.filter((p) => {
      const d = new Date(p.proposed_time)
      const isInCurrentWeek =
        d >= firstDayOfWeek && d < addDays(lastDayOfWeek, 1)

      if (!isInCurrentWeek) return false

      // Exclude expired proposals
      if (p.expires_at && new Date(p.expires_at) <= now) return false

      // Exclude canceled, deleted, scheduled, or dismissed proposals
      if (
        p.status === 'canceled' ||
        p.status === 'deleted' ||
        p.status === 'scheduled' ||
        p.status === 'dismissed'
      ) {
        return false
      }

      // Include declined proposals only if they were sent by the user
      if (p.status === 'declined') {
        return p.is_sent_by_user
      }

      return true
    })

    // Then filter out proposals that have corresponding matches
    const filtered = currentWeekProposals.filter((proposal) => {
      const hasCorrespondingMatch = allMatches.some((match) => {
        return isSameDateTime(proposal.proposed_time, match.match_datetime)
      })
      return !hasCorrespondingMatch
    })
    return filtered
  }, [
    matchProposals,
    firstDayOfWeek,
    lastDayOfWeek,
    allMatches,
    isSameDateTime,
  ])

  const weekScheduledMatches = useMemo(() => {
    return allMatches.filter((m) => {
      if (!m.match_datetime) return false
      // Only include matches that should appear on calendar views.
      // This prevents newly-created/unscheduled matches with default timestamps
      // (and any future/unknown statuses) from leaking onto the calendar.
      const calendarStatuses = new Set([
        'scheduled',
        'normal',
        'home_retired',
        'away_retired',
        'i_retired',
        'opponent_retired',
      ])
      if (!calendarStatuses.has(m.status)) return false
      const d = new Date(m.match_datetime)
      return d >= firstDayOfWeek && d < addDays(lastDayOfWeek, 1)
    })
  }, [allMatches, firstDayOfWeek, lastDayOfWeek])

  // Helper functions
  const formatTimeSlot = useCallback((timeSlot) => {
    const { hour, minute } = timeSlot
    const ampm = hour >= 12 ? 'PM' : 'AM'
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour
    const minuteStr = minute === 0 ? '00' : '30'
    return `${displayHour}:${minuteStr} ${ampm}`
  }, [])

  const isUserAvailable = useCallback(
    (date, timeSlot) => {
      const dateKey = format(date, 'yyyy-MM-dd')
      const key = `${dateKey}-${timeSlot.hour}-${timeSlot.minute}`

      return availabilityMap.has(key)
    },
    [availabilityMap],
  )

  const getOpponentsAvailable = useCallback(
    (date, timeSlot) => {
      const availableOpponents = []

      expandedOpponentAvailability.forEach((slot) => {
        if (
          utcSlotContainsLocalTime(
            slot.start_time,
            slot.end_time,
            date,
            timeSlot.hour,
            timeSlot.minute,
          )
        ) {
          const opponent = unplayedOpponents.find(
            (opp) => opp.user_id === slot.opponent_user_id,
          )
          if (opponent) {
            availableOpponents.push(opponent)
          }
        }
      })

      return availableOpponents
    },
    [expandedOpponentAvailability, unplayedOpponents],
  )

  const getUserAvailabilityForSlot = useCallback(
    (date, timeSlot) => {
      const dateKey = format(date, 'yyyy-MM-dd')
      const key = `${dateKey}-${timeSlot.hour}-${timeSlot.minute}`
      const slots = availabilityMap.get(key)
      return slots ? slots[0] : null
    },
    [availabilityMap],
  )

  // Navigation functions
  const navigateWeek = useCallback(
    (direction) => {
      const daysToAdd = direction === 'next' ? 7 : -7
      const newWeek = addDays(currentWeek, daysToAdd)
      setCurrentWeek(newWeek)
      setSearchParams({ week: format(newWeek, 'yyyy-MM-dd') })
    },
    [currentWeek, setSearchParams],
  )

  const goToToday = useCallback(() => {
    const thisWeek = startOfWeek(new Date())
    setCurrentWeek(thisWeek)
    setSearchParams({ week: format(thisWeek, 'yyyy-MM-dd') })
  }, [setSearchParams])

  // Opponent processing functions
  const processOpponentSlots = useCallback((slots) => {
    const now = new Date()

    const futureSlots = slots.filter((slot) => {
      const slotStart = asDate(slot.start_time)
      return slotStart > now
    })

    if (futureSlots.length === 0) {
      return []
    }

    const sortedSlots = futureSlots.sort((a, b) => {
      return asDate(a.start_time) - asDate(b.start_time)
    })

    const combinedSlots = []
    let currentCombined = null

    sortedSlots.forEach((slot) => {
      const slotStart = asDate(slot.start_time)
      const slotEnd = asDate(slot.end_time)

      if (!currentCombined) {
        currentCombined = {
          start_time: slot.start_time,
          end_time: slot.end_time,
          date: format(slotStart, 'MMM d, yyyy'),
          originalSlots: [slot],
        }
      } else {
        const currentEnd = asDate(currentCombined.end_time)

        if (
          isSameDay(slotStart, asDate(currentCombined.start_time)) &&
          slotStart <= currentEnd
        ) {
          if (slotEnd > currentEnd) {
            currentCombined.end_time = slot.end_time
          }
          currentCombined.originalSlots.push(slot)
        } else {
          combinedSlots.push(currentCombined)
          currentCombined = {
            start_time: slot.start_time,
            end_time: slot.end_time,
            date: format(slotStart, 'MMM d, yyyy'),
            originalSlots: [slot],
          }
        }
      }
    })

    if (currentCombined) {
      combinedSlots.push(currentCombined)
    }

    return combinedSlots
  }, [])

  const hasUserOverlap = useCallback(
    (combinedSlot) => {
      const slotStart = asDate(combinedSlot.start_time)
      const slotEnd = asDate(combinedSlot.end_time)

      return userAvailability.some((userSlot) => {
        const userStart = asDate(userSlot.start_time)
        const userEnd = asDate(userSlot.end_time)

        return (
          isSameDay(slotStart, userStart) &&
          slotStart < userEnd &&
          slotEnd > userStart
        )
      })
    },
    [userAvailability],
  )

  // Match proposal and scheduled match lookup functions
  const getMatchProposalForOpponent = useCallback(
    (opponentId, date, hour, slotEndTime = null) => {
      // date can be Date or string; normalize to Date
      const base = date instanceof Date ? new Date(date) : new Date(date)

      // If we have a slot end time, use the full date with actual time
      // Otherwise, use the hour parameter and zero out minutes (legacy behavior)
      if (slotEndTime) {
        // Keep the date as-is - it should already have the correct start time
        // No need to modify hours/minutes
      } else {
        // Legacy behavior: set to top of the hour
        base.setHours(hour, 0, 0, 0)
      }

      const found = matchProposals.find((proposal) => {
        if (proposal.status === 'canceled' || proposal.status === 'deleted') {
          return false
        }

        if (!proposalInvolvesPlayer(proposal, opponentId)) return false

        const proposalTime = new Date(proposal.proposed_time)

        // Check if same day first
        const sameDay =
          proposalTime.getDate() === base.getDate() &&
          proposalTime.getMonth() === base.getMonth() &&
          proposalTime.getFullYear() === base.getFullYear()

        if (!sameDay) return false

        // If we have a slot end time, check if proposal falls within the slot window
        if (slotEndTime) {
          const slotEnd = new Date(slotEndTime)
          return proposalTime >= base && proposalTime < slotEnd
        }

        // Otherwise, check if proposal is in the same hour as the slot start
        const proposalHour = proposalTime.getHours()
        const baseHour = base.getHours()
        return proposalHour === baseHour
      })
      return found
    },
    [matchProposals],
  )

  const getLatestProposalForOpponent = useCallback(
    (opponentId) => {
      const proposals = matchProposals
        .filter((proposal) => {
          if (proposal.status === 'canceled' || proposal.status === 'deleted') {
            return false
          }

          return proposalInvolvesPlayer(proposal, opponentId)
        })
        .sort((a, b) => {
          const timeA = a.proposed_time
            ? new Date(a.proposed_time).getTime()
            : 0
          const timeB = b.proposed_time
            ? new Date(b.proposed_time).getTime()
            : 0
          return timeB - timeA
        })

      return proposals[0] || null
    },
    [matchProposals],
  )

  const getScheduledMatchForOpponent = useCallback(
    (opponentId, date, hour) => {
      const base = date instanceof Date ? new Date(date) : new Date(date)
      base.setHours(hour, 0, 0, 0)

      const found = allMatches.find((match) => {
        if (!match.match_datetime) return false
        // Exclude canceled matches (status 'created' means unscheduled/canceled)
        if (match.status === 'created') return false

        const matchTime = new Date(match.match_datetime)
        const timeDiff = Math.abs(matchTime.getTime() - base.getTime())

        const isOpponentMatch =
          match.home_player_id === opponentId ||
          match.away_player_id === opponentId

        return timeDiff < 3600000 && isOpponentMatch
      })
      return found || null
    },
    [allMatches],
  )

  const getExistingMatchWithOpponent = useCallback(
    (opponentId) => {
      // Find any scheduled or played match with this opponent
      const match = allMatches.find((match) => {
        // Exclude canceled matches (status 'created' means unscheduled/canceled)
        if (match.status === 'created') return false

        const isOpponentMatch =
          match.home_player_id === opponentId ||
          match.away_player_id === opponentId

        return isOpponentMatch
      })
      return match || null
    },
    [allMatches],
  )

  // Action handlers
  const handleMatchProposalConfirm = useCallback(
    async (
      opponentPlayerId,
      proposedTime,
      leagueId,
      expiresAt,
      notes,
      directProposal,
    ) => {
      try {
        const result = await services.matchProposal.createProposal(
          opponentPlayerId,
          proposedTime,
          leagueId,
          divisionInfo?.division_id,
          expiresAt,
          {
            notes,
            directProposal,
          },
        )

        if (result.success) {
          await loadAvailabilityData()
        }
      } catch {
        // Error creating match proposal
        toastr.error('Failed to send match proposal')
      }
    },
    [loadAvailabilityData, divisionInfo],
  )

  const handleAvailabilitySave = useCallback(
    async (availabilityData, modalMode) => {
      // Ensure we have league and division context
      if (!divisionInfo || !leagueId) {
        throw new Error(
          'League and division information is required. Please wait for data to load and try again.',
        )
      }

      // Add league and division context to availability data
      const enrichedData = {
        ...availabilityData,
        league_id: parseInt(leagueId),
        division_id: divisionInfo.division_id,
      }

      // Check if this is actually an update or create based on presence of id
      // (needed for recurring availability single-instance updates which add exception + create new)
      let saveResult
      if (enrichedData.apply_to === 'team') {
        const teamScopedData = {
          ...enrichedData,
          team_id: enrichedData.team_id || getCurrentTeamId(divisionInfo),
        }

        if (modalMode === 'edit' && teamScopedData.shared_team_slot_id) {
          saveResult =
            await services.availability.updateTeamAvailability(teamScopedData)
        } else {
          saveResult =
            await services.availability.createTeamAvailability(teamScopedData)
          if (saveResult.success && modalMode === 'edit' && enrichedData.id) {
            await services.availability.deleteAvailability(enrichedData.id)
          }
        }
      } else if (modalMode === 'edit' && enrichedData.shared_team_slot_id) {
        const individualData = { ...enrichedData }
        delete individualData.id
        delete individualData.shared_team_slot_id
        delete individualData.team_id
        saveResult =
          await services.availability.createAvailability(individualData)
        if (saveResult.success) {
          await services.availability.deleteTeamAvailability(
            enrichedData.shared_team_slot_id,
          )
        }
      } else if (modalMode === 'edit' && enrichedData.id) {
        saveResult =
          await services.availability.updateAvailability(enrichedData)
      } else {
        saveResult =
          await services.availability.createAvailability(enrichedData)
      }

      if (!saveResult.success) {
        throw new Error(saveResult.error || 'Failed to save availability')
      }

      await loadAvailabilityData()
    },
    [loadAvailabilityData, divisionInfo, leagueId],
  )

  const handleAvailabilityDelete = useCallback(
    async (availabilityId, exceptionDate = null, options = {}) => {
      if (options.sharedTeamSlotId && exceptionDate) {
        await services.availability.addTeamException(
          options.sharedTeamSlotId,
          exceptionDate,
        )
      } else if (options.sharedTeamSlotId) {
        await services.availability.deleteTeamAvailability(
          options.sharedTeamSlotId,
        )
      } else if (exceptionDate) {
        // Add exception to recurring availability (delete just this instance)
        await services.availability.addException(availabilityId, exceptionDate)
      } else {
        // Delete the entire availability
        await services.availability.deleteAvailability(availabilityId)
      }
      await loadAvailabilityData()
    },
    [loadAvailabilityData],
  )

  const handleProposalAction = useCallback(async () => {
    await loadAvailabilityData()
  }, [loadAvailabilityData])

  // Helper for creating opponent click data
  const createOpponentClickData = useCallback(
    (opponent, date, hour, minute = 0) => {
      // build a true UTC ISO from the local wall-clock time:
      const local = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        hour,
        minute,
      )
      const timeToSend = local.toISOString()

      return {
        opponent,
        proposedTime: timeToSend,
      }
    },
    [],
  )

  return {
    // State
    loading,
    error,
    currentWeek,
    userAvailability: expandedUserAvailability,
    opponentAvailability: expandedOpponentAvailability,
    unplayedOpponents,
    matchProposals,
    allMatches,
    divisionRoster,
    currentUserPlayerId,
    divisionInfo,

    // Derived data
    weekDates,
    weekProposals,
    weekScheduledMatches,
    availabilityMap,

    // Constants
    TIME_SLOTS,
    DAYS,

    // Helper functions
    formatTimeSlot,
    isUserAvailable,
    getOpponentsAvailable,
    getUserAvailabilityForSlot,
    processOpponentSlots,
    hasUserOverlap,
    getMatchProposalForOpponent,
    getLatestProposalForOpponent,
    getScheduledMatchForOpponent,
    getExistingMatchWithOpponent,
    createOpponentClickData,

    // Navigation
    navigateWeek,
    goToToday,

    // Actions
    handleMatchProposalConfirm,
    handleAvailabilitySave,
    handleAvailabilityDelete,
    handleProposalAction,
    loadAvailabilityData,
  }
}
